import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, Medal } from 'lucide-react';
import type { CustomerTierToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SystemGrid, type SystemGridColumn } from '~/components/system/grid/system-grid';
import { serverMessage } from '~/lib/api/server-error';
import { useCustomerTiersList, useCustomerTierMutations } from '~/hooks/use-customer-tiers';
import { tokenToForm, type CustomerTierFormValues } from '~/lib/schemas/customer-tier';
import { CustomerTierEditDialog } from './tier-edit-dialog';

const Bool = ({ on }: { on: boolean }) =>
	on ? <Check size={16} className="text-success-fg" /> : <span className="text-foreground-disabled">—</span>;

/** Customer tiers (Βαθμίδες Πελατών) CRUD surface — parity with the legacy admin grid. */
export function CustomerTiersGridView() {
	const { t } = useTranslation();
	const { data, isLoading, isError, refetch } = useCustomerTiersList();
	const { remove } = useCustomerTierMutations();

	const [editOpen, setEditOpen] = useState(false);
	const [editing, setEditing] = useState<CustomerTierFormValues | null>(null);
	const [deleteRow, setDeleteRow] = useState<CustomerTierToken | null>(null);

	const columns = useMemo<SystemGridColumn<CustomerTierToken>[]>(
		() => [
			{
				key: 'name',
				header: t('customers.tiers.columns.name'),
				width: 'minmax(0,1fr)',
				sortValue: o => o.nm ?? '',
				exportValue: o => o.nm ?? '',
				render: o => (
					<span className="truncate text-sm font-medium text-foreground" title={o.nm ?? ''}>
						{o.nm || '—'}
					</span>
				)
			},
			{
				key: 'code',
				header: t('customers.tiers.columns.code'),
				width: '160px',
				sortValue: o => o.cd ?? '',
				exportValue: o => o.cd ?? '',
				render: o => <span className="truncate font-mono text-xs text-foreground-muted">{o.cd || '—'}</span>
			},
			{
				key: 'level',
				header: t('customers.tiers.columns.level'),
				width: '90px',
				align: 'right',
				sortValue: o => o.lvl ?? 0,
				exportValue: o => o.lvl ?? 0,
				render: o => <span className="text-sm tabular-nums text-foreground-muted">{o.lvl ?? 0}</span>
			},
			{
				key: 'multiplier',
				header: t('customers.tiers.columns.multiplier'),
				width: '110px',
				align: 'right',
				sortValue: o => o.multiplier ?? 0,
				exportValue: o => o.multiplier ?? '',
				render: o => <span className="text-sm tabular-nums text-foreground-muted">{o.multiplier ?? '—'}</span>
			},
			{
				key: 'color',
				header: t('customers.tiers.columns.color'),
				width: '80px',
				align: 'center',
				sortValue: o => o.chex ?? '',
				exportValue: o => o.chex ?? '',
				render: o =>
					o.chex ? (
						<span
							className="inline-block h-4 w-4 rounded-full border border-border align-middle"
							style={{ backgroundColor: o.chex }}
							title={o.chex}
						/>
					) : (
						<span className="text-foreground-disabled">—</span>
					)
			},
			{
				key: 'visible',
				header: t('customers.tiers.columns.visible'),
				width: '80px',
				align: 'center',
				sortValue: o => (o.iv ? 1 : 0),
				exportValue: o => (o.iv ? t('common.yes') : t('common.no')),
				render: o => <Bool on={Boolean(o.iv)} />
			},
			{
				key: 'active',
				header: t('customers.tiers.columns.active'),
				width: '80px',
				align: 'center',
				sortValue: o => (o.act ? 1 : 0),
				exportValue: o => (o.act ? t('common.yes') : t('common.no')),
				render: o => <Bool on={Boolean(o.act)} />
			},
			{
				key: 'sort',
				header: t('customers.tiers.columns.sort'),
				width: '80px',
				align: 'right',
				sortValue: o => o.si ?? 0,
				exportValue: o => o.si ?? 0,
				render: o => <span className="text-sm tabular-nums text-foreground-muted">{o.si}</span>
			}
		],
		[t]
	);

	const confirmDelete = () => {
		if (!deleteRow?.id) return;
		remove.mutate(deleteRow.id, {
			onSuccess: () => {
				toast.success(t('common.delete'));
				setDeleteRow(null);
			},
			onError: (err: unknown) => toast.error(serverMessage(err))
		});
	};

	return (
		<>
			<SystemGrid<CustomerTierToken>
				gridId="customer-tiers"
				title={t('customers.tiers.title')}
				rows={data ?? []}
				loading={isLoading}
				error={isError}
				onRetry={() => void refetch()}
				getRowId={o => o.id ?? o.nm ?? ''}
				columns={columns}
				searchPredicate={(o, q) => (o.nm ?? '').toLowerCase().includes(q) || (o.cd ?? '').toLowerCase().includes(q)}
				defaultSort={{ key: 'level', dir: 'asc' }}
				emptyIcon={<Medal size={36} className="text-foreground-disabled" strokeWidth={1.25} />}
				addLabel={t('customers.tiers.add')}
				onAdd={() => {
					setEditing(null);
					setEditOpen(true);
				}}
				onEdit={o => {
					setEditing(tokenToForm(o));
					setEditOpen(true);
				}}
				onDelete={setDeleteRow}
				exportFilename="customer-tiers"
			/>

			<CustomerTierEditDialog open={editOpen} onOpenChange={setEditOpen} initial={editing} />

			<Dialog open={Boolean(deleteRow)} onOpenChange={open => !open && setDeleteRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('customers.tiers.deleteTitle')}</DialogTitle>
						<DialogDescription>{t('customers.tiers.deleteBody', { name: deleteRow?.nm || '' })}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeleteRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending}>
							{t('common.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
